import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import Rails from "@rails/ujs";
import GoogleLogo from "@shared/components/GoogleLogo";
import LargeButton from "@shared/components/LargeButton";
import { useCsrfToken } from "@shared/hooks/useCSRFToken";

interface Props {
  isDisabled?: boolean;
  referral?: string;
  partner?: string;
  utmSource?: string;
  utmCampaign?: string;
  utmData?: Record<string, any>;
  internalRef?: string;
  adrollClickId?: string;
  gaClientId?: string;
  gaSessionId?: string;
  fbclid?: string;
  fbp?: string;
  fbc?: string;
  liFatId?: string;
  googleClickId?: string;
  adrollFirstPartyCookie?: string;
  posthogDistinctId?: string;
  darkModeAllowed?: boolean;
}

function GoogleSSOButton({
  isDisabled = false,
  referral,
  partner,
  utmSource,
  utmCampaign,
  utmData,
  internalRef,
  adrollClickId,
  gaClientId,
  gaSessionId,
  fbclid,
  fbp,
  fbc,
  liFatId,
  googleClickId,
  adrollFirstPartyCookie,
  posthogDistinctId,
  darkModeAllowed = true,
}: Props) {
  const { refreshCsrfToken } = useCsrfToken("/");
  const authenticityTokenInputRef = React.useRef<HTMLInputElement>(null);

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const freshToken = await refreshCsrfToken();
    if (authenticityTokenInputRef.current) {
      authenticityTokenInputRef.current.value = freshToken;
    }
    formElement.submit();
  };

  return (
    <form action="/api/v1/users/auth/google_oauth2" method="post" onSubmit={handleFormSubmit}>
      <Styled.GoogleButton type="submit" disabled={isDisabled} styleType="largeSecondary" useBreakpoint={false} darkModeAllowed={darkModeAllowed}>
        <GoogleLogo size={24} />
        Continue with Google
      </Styled.GoogleButton>

      {/* CSRF */}
      <input
        ref={authenticityTokenInputRef}
        name="authenticity_token"
        type="hidden"
        value={Rails.csrfToken() ? Rails.csrfToken() : ""}
      />

      {/* Tracking params (render only when present) */}
      {typeof referral === "string" && referral.length > 0 ? (
        <input type="hidden" name="referral" value={referral} />
      ) : null}
      {typeof partner === "string" && partner.length > 0 ? (
        <input type="hidden" name="partner" value={partner} />
      ) : null}
      {typeof utmSource === "string" && utmSource.length > 0 ? (
        <input type="hidden" name="utm_source" value={utmSource} />
      ) : null}
      {typeof utmCampaign === "string" && utmCampaign.length > 0 ? (
        <input type="hidden" name="utm_campaign" value={utmCampaign} />
      ) : null}
      {typeof internalRef === "string" && internalRef.length > 0 ? (
        <input type="hidden" name="internal_ref" value={internalRef} />
      ) : null}
      {typeof adrollClickId === "string" && adrollClickId.length > 0 ? (
        <input type="hidden" name="adroll_click_id" value={adrollClickId} />
      ) : null}
      {typeof gaClientId === "string" && gaClientId.length > 0 ? (
        <input type="hidden" name="ga_client_id" value={gaClientId} />
      ) : null}
      {typeof gaSessionId === "string" && gaSessionId.length > 0 ? (
        <input type="hidden" name="ga_session_id" value={gaSessionId} />
      ) : null}
      {typeof fbclid === "string" && fbclid.length > 0 ? (
        <input type="hidden" name="fbclid" value={fbclid} />
      ) : null}
      {typeof fbp === "string" && fbp.length > 0 ? (
        <input type="hidden" name="fbp" value={fbp} />
      ) : null}
      {typeof fbc === "string" && fbc.length > 0 ? (
        <input type="hidden" name="fbc" value={fbc} />
      ) : null}
      {typeof liFatId === "string" && liFatId.length > 0 ? (
        <input type="hidden" name="li_fat_id" value={liFatId} />
      ) : null}
      {typeof googleClickId === "string" && googleClickId.length > 0 ? (
        <input type="hidden" name="google_click_id" value={googleClickId} />
      ) : null}
      {typeof adrollFirstPartyCookie === "string" && adrollFirstPartyCookie.length > 0 ? (
        <input type="hidden" name="adroll_first_party_cookie" value={adrollFirstPartyCookie} />
      ) : null}
      {typeof posthogDistinctId === "string" && posthogDistinctId.length > 0 ? (
        <input type="hidden" name="posthog_distinct_id" value={posthogDistinctId} />
      ) : null}
      {utmData != undefined
        ? Object.keys(utmData).map((key) =>
            typeof utmData[key] === "string" && utmData[key].length > 0 ? (
              <input type="hidden" name={"utm_data[" + key + "]"} value={utmData[key]} key={key} />
            ) : null,
          )
        : null}
    </form>
  );
}

export default GoogleSSOButton;

/* Styled Components */
let Styled: any = {};
Styled.GoogleButton = styled(LargeButton)((props: any) => {
  const t: any = props.theme;
  const darkModeAllowed = props.darkModeAllowed;
  return css`
    label: GoogleSSOButton;
    background-color: ${darkModeAllowed && t.dark ? t.color.gray[700] : t.color.white};
    color: ${darkModeAllowed && t.dark ? t.color.gray[200] : t.color.black};
    border: 1px solid ${darkModeAllowed && t.dark ? "transparent" : t.color.gray[300]};
    width: 100%;
    height: 40px;

    &:link,
    &:visited {
      color: ${darkModeAllowed && t.dark ? t.color.gray[200] : t.color.black};
    }

    &:hover:not([disabled]) {
      color: ${darkModeAllowed && t.dark ? t.color.white : t.color.black};
      background-color: ${darkModeAllowed && t.dark ? t.color.gray[600] : t.color.gray[50]};
    }

    &:focus {
      box-shadow: 0 0 0 2px ${darkModeAllowed && t.dark ? t.color.gray[500] : t.color.gray[300]};
    }

    &:hover:focus {
      box-shadow: 0 0 0 2px ${darkModeAllowed && t.dark ? t.color.gray[500] : t.color.gray[300]};
    }

    span {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      width: 100%;
      flex-wrap: nowrap;
    }
  `;
});
